import React, { useState, useMemo, ReactNode } from "react";
import {
  FiChevronUp as FiChevronUpRaw,
  FiChevronDown as FiChevronDownRaw,
  FiChevronsUp as FiChevronsUpRaw,
  FiEye as FiEyeRaw,
  FiEdit2 as FiEdit2Raw,
  FiTrash2 as FiTrash2Raw,
  FiChevronLeft as FiChevronLeftRaw,
  FiChevronRight as FiChevronRightRaw,
  FiInbox as FiInboxRaw,
} from "react-icons/fi";

// npm install react-icons
// Cast to a plain FC<{size?:number}> — works around a react-icons/React 19
// type mismatch (IconType returns ReactNode, JSX wants ReactElement).
type IconFC = React.FC<{ size?: number; className?: string }>;
const FiChevronUp = FiChevronUpRaw as IconFC;
const FiChevronDown = FiChevronDownRaw as IconFC;
const FiChevronsUp = FiChevronsUpRaw as IconFC;
const FiEye = FiEyeRaw as IconFC;
const FiEdit2 = FiEdit2Raw as IconFC;
const FiTrash2 = FiTrash2Raw as IconFC;
const FiChevronLeft = FiChevronLeftRaw as IconFC;
const FiChevronRight = FiChevronRightRaw as IconFC;
const FiInbox = FiInboxRaw as IconFC;

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  render?: (row: T) => ReactNode;
}

export interface Action<T> {
  label: string | ((row: T) => string);
  icon?: "view" | "edit" | "delete" | ReactNode | ((row: T) => ReactNode);
  onClick: (row: T) => void;
  danger?: boolean | ((row: T) => boolean);
  hidden?: (row: T) => boolean;
  disabled?:boolean | ((row: T) => boolean)
}
interface Props<T> {
  columns: Column<T>[];
  data: T[];
  actions?: Action<T>[];
  pageSize?: number;
  rowKey?: string;
  emptyMessage?: string;
  disbled?:boolean;
  totalPages?:number,
  
}

const getValue = (row: any, key: string) => 
  key.split(".").reduce((v, k) => (v == null ? undefined : v[k]), row);

const ICONS: Record<string, IconFC> = {
  view: FiEye,
  edit: FiEdit2,
  delete: FiTrash2,
};

const ALIGN: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export default function DataTable<T>({
  columns,
  data,
  actions = [],
  pageSize = 5,
  rowKey = "id",
  emptyMessage = "No records found",
  totalPages=0
  

}: Props<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  // console.log("show data--->",data);
  

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const av = getValue(a, sortKey);
      const bv = getValue(b, sortKey);
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortAsc
        ? String(av).localeCompare(String(bv), undefined, { numeric: true })
        : String(bv).localeCompare(String(av), undefined, { numeric: true });
    });
  }, [data, sortKey, sortAsc]);

  // const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const toggleSort = (col: Column<T>) => {
    if (!col.sortable) return;
    if (sortKey === col.key) setSortAsc(!sortAsc);
    else {
      setSortKey(col.key);
      setSortAsc(true);
    }
    setPage(1);
  };

  return (
    <div className="w-full flex flex-col min-h-[490px]">
      {/* <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white"> */}
      <div className="flex-1 overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col)}
                  className={`px-5 py-3.5 font-semibold text-slate-500 uppercase text-[11px] tracking-wider whitespace-nowrap select-none ${
                    ALIGN[col.align || "left"]
                  } ${col.sortable ? "cursor-pointer hover:text-slate-800" : ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.header}
                    {col.sortable &&
                      (sortKey === col.key ? (
                        sortAsc ? (
                          <FiChevronUp size={14} />
                        ) : (
                          <FiChevronDown size={14} />
                        )
                      ) : (
                        <FiChevronsUp size={13} className="opacity-30" />
                      ))}
                  </span>
                </th>
              ))}
              {actions.length > 0 && (
                <th className="px-5 py-3.5 text-left font-semibold text-slate-500 uppercase text-[11px] tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-16">
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <FiInbox size={28} />
                    <span className="text-sm">{emptyMessage}</span>
                  </div>
                </td>
              </tr>
            ) : (
              pageData.map((row, i) => (
                <tr
                  key={(row as any)[rowKey] ?? i}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80 transition-colors"
                >
                  {columns.map((col) => (
                    <td key={col.key} className={`px-5 py-3.5 text-slate-700 ${ALIGN[col.align || "left"]}`}>
                      {col.render ? col.render(row) : String(getValue(row, col.key) ?? "")}
                    </td>
                  ))}
               
    <div className="inline-flex items-center gap-1">
      {actions.length > 0 && (
  <td className="px-5 py-3.5 text-right whitespace-nowrap">
    <div className="inline-flex items-center gap-1">
      {actions.map((a, idx) => {
        if (a.hidden && a.hidden(row)) return null;
        const label = typeof a.label === "function" ? a.label(row) : a.label;
        const iconVal = typeof a.icon === "function" ? a.icon(row) : a.icon;
        const danger = typeof a.danger === "function" ? a.danger(row) : a.danger;
        const isDisabled = typeof a.disabled === "function" ? a.disabled(row) : a.disabled;
        const Icon = typeof iconVal === "string" ? ICONS[iconVal] : null;
        return (
          <button
            key={idx}
            title={label}
            disabled={isDisabled}
            onClick={() => !isDisabled && a.onClick(row)}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
              danger
                ? "text-red-500 border-red-100 hover:bg-red-50 hover:border-red-200"
                : "text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-slate-700"
            }`}
          >
            {Icon ? <Icon size={15} /> : (iconVal as ReactNode) ?? label}
          </button>
        );
      })}
    </div>
  </td>
)}
    </div>
  
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
        <span>
          Page <span className="font-medium text-slate-700">{current}</span> of{" "}
          <span className="font-medium text-slate-700">{totalPages}</span>
        </span>
        <div className="flex items-center gap-1.5">
          <button
            disabled={current === 1}
            onClick={() => setPage((p) => p - 1)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:bg-slate-100"
          >
            <FiChevronLeft size={16} />
          </button>
          <button
            disabled={current === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:bg-slate-100"
          >
            <FiChevronRight size={16} />
          </button>
        </div>
      </div> */}
      <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
  {/* Left */}
  <div>
    <p className="text-sm text-slate-500">
      Showing page{" "}
      <span className="font-semibold text-slate-800">{current}</span> of{" "}
      <span className="font-semibold text-slate-800">{totalPages}</span>
    </p>
  </div>

  {/* Right */}
  <div className="flex items-center gap-2">
    {/* Previous */}
    <button
      disabled={current === 1}
      onClick={() => setPage((p) => p - 1)}
      className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition-all duration-200 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 disabled:pointer-events-none disabled:opacity-40"
    >
      <FiChevronLeft size={16} />
      Previous
    </button>

    {/* Page Numbers */}
    {Array.from({ length: totalPages }, (_, index) => {
      const page = index + 1;

      return (
        <button
          key={page}
          onClick={() => setPage(page)}
          className={`h-10 w-10 rounded-xl text-sm font-semibold transition-all duration-200 ${
            current === page
              ? "bg-blue-600 text-white shadow-lg shadow-blue-100"
              : "border border-slate-200 bg-white text-slate-600 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600"
          }`}
        >
          {page}
        </button>
      );
    })}

    {/* Next */}
    <button
      disabled={current === totalPages}
      onClick={() => setPage((p) => p + 1)}
      className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition-all duration-200 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 disabled:pointer-events-none disabled:opacity-40"
    >
      Next
      <FiChevronRight size={16} />
    </button>
  </div>
</div>
    </div>
  );
}